/**
 * File Upload Button Component
 * Supports: PDF, DOCX, TXT, PNG, JPG
 * Max: 3 files, 10MB each (general); 1 file, 50MB (engineering)
 *
 * Props:
 *   mode       - 'general' (default) | 'engineering'
 *   userId     - user email, required when mode='engineering' (X-Uploaded-By)
 */

import React, { useRef, useState } from 'react';
import { FaPaperclip, FaSpinner } from 'react-icons/fa';
import { API_BASE_URL } from '../api/apiConfig';
import { uploadEngFile, pollEngJobStatus } from '../api/engUploadApi';
import './UploadButton.css';

const UploadButton = ({ disabled = false, onUploadSuccess, onUploadError, onShowToast, accessToken, sessionId, mode = 'general', userId = '' }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [processingDocs, setProcessingDocs] = useState([]); // Track documents being processed

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  // ── General mode: poll general backend status ───────────────────────────────
  const pollDocumentStatus = async (docId, filename) => {
    const maxAttempts = 150; // 150 attempts * 2 seconds = 5 minutes max (needed for large files)
    let attempts = 0;

    const poll = async () => {
      try {
        const headers = {
          'Authorization': `Bearer ${accessToken}`
        };

        if (sessionId) {
          headers['X-Session-Id'] = sessionId;
        }

        const response = await fetch(`${API_BASE_URL}/api/documents/${docId}/status`, {
          method: 'GET',
          headers: headers
        });

        if (!response.ok) {
          throw new Error('Status check failed');
        }

        const statusData = await response.json();
        console.log(`Status for ${filename}:`, statusData.status);

        if (statusData.status === 'ready') {
          // Document is ready!
          setProcessingDocs(prev => prev.filter(id => id !== docId));
          console.log(`✅ ${filename} is ready!`);
          
          // Notify user that document is ready
          if (onShowToast) {
            onShowToast(`${filename} is ready! You can now chat with this document.`, 'success');
          }
          
          return true;
        } else if (statusData.status === 'failed') {
          // Processing failed
          setProcessingDocs(prev => prev.filter(id => id !== docId));
          console.error(`❌ ${filename} processing failed: ${statusData.message}`);
          return false;
        } else if (statusData.status === 'processing') {
          // Still processing
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000); // Poll every 2 seconds
          } else {
            console.warn(`⏱️ ${filename} processing timeout`);
            setProcessingDocs(prev => prev.filter(id => id !== docId));
          }
        }
      } catch (error) {
        console.error(`Error polling status for ${filename}:`, error);
        setProcessingDocs(prev => prev.filter(id => id !== docId));
      }
    };

    // Start polling
    setTimeout(poll, 2000); // First check after 2 seconds
  };

  // ── Engineering mode: upload one file then poll eng service ─────────────────
  const handleEngFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedEngExtensions = ['.pdf', '.docx', '.png', '.jpg', '.jpeg'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedEngExtensions.includes(ext)) {
      const error = `File type not supported in Engineering mode: ${ext}. Allowed: PDF, DOCX, PNG, JPG`;
      if (onShowToast) onShowToast(error, 'error');
      if (onUploadError) onUploadError(error);
      event.target.value = '';
      return;
    }

    const maxSizeBytes = 50 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const error = `File too large: ${file.name}. Maximum size is 50 MB`;
      if (onShowToast) onShowToast(error, 'error');
      if (onUploadError) onUploadError(error);
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      // Step 1 — POST to eng service
      const uploadRes = await uploadEngFile(file, userId);
      const { job_id, doc_id, filename, blob_url } = uploadRes;
      console.log(`📤 Eng upload queued: job_id=${job_id} doc_id=${doc_id}`);

      setProcessingDocs(prev => [...prev, doc_id]);
      if (onShowToast) onShowToast(`${filename} uploaded! Processing in the background...`, 'success');

      if (onUploadSuccess) {
        onUploadSuccess({ documents: [{ id: doc_id, doc_id, filename, content_kind: ext.replace('.', ''), blob_url: blob_url || null }] });
      }

      // Step 2 — Poll in background (non-blocking)
      pollEngJobStatus(job_id, (status) => {
        console.log(`📊 Eng job ${job_id}: ${status}`);
      }).then((finalJob) => {
        setProcessingDocs(prev => prev.filter(id => id !== doc_id));
        if (finalJob.status === 'ready') {
          console.log(`✅ Eng doc ${filename} is ready (${finalJob.chunk_count} chunks)`);
          if (onShowToast) onShowToast(`${filename} is ready! You can now ask questions about it.`, 'success');
        } else {
          console.error(`❌ Eng doc ${filename} failed: ${finalJob.error_message}`);
          if (onShowToast) onShowToast(`Processing failed for ${filename}: ${finalJob.error_message || 'Unknown error'}`, 'error');
        }
      }).catch((err) => {
        setProcessingDocs(prev => prev.filter(id => id !== doc_id));
        console.error('Eng polling error:', err);
        if (onShowToast) onShowToast(`Could not confirm processing status for ${filename}. Check the Engineering Document Manager.`, 'warning');
      });

    } catch (error) {
      console.error('Eng upload error:', error);
      if (onShowToast) onShowToast(`Upload failed: ${error.message}`, 'error');
      if (onUploadError) onUploadError(error.message);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  // ── General mode file change handler ────────────────────────────────────────
  const handleFileChange = async (event) => {
    if (mode === 'engineering') {
      return handleEngFileChange(event);
    }

    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    // Validate number of files
    if (files.length > 3) {
      const error = 'Maximum 3 files allowed per upload';
      if (onShowToast) onShowToast(error, 'error');
      if (onUploadError) onUploadError(error);
      return;
    }

    // Validate file sizes and types
    const allowedExtensions = ['.pdf', '.docx', '.txt', '.png', '.jpg', '.jpeg'];
    const maxSizeBytes = 50 * 1024 * 1024; // 50MB

    for (const file of files) {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedExtensions.includes(extension)) {
        const error = `File type not supported: ${extension}. Allowed: PDF, DOCX, TXT, PNG, JPG`;
        if (onShowToast) onShowToast(error, 'error');
        if (onUploadError) onUploadError(error);
        return;
      }

      if (file.size > maxSizeBytes) {
        const error = `File too large: ${file.name}. Maximum size is 50MB`;
        if (onShowToast) onShowToast(error, 'error');
        if (onUploadError) onUploadError(error);
        return;
      }
    }

    // Upload files
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const headers = {
        'Authorization': `Bearer ${accessToken}`
      };

      if (sessionId) {
        headers['X-Session-Id'] = sessionId;
      }

      const response = await fetch(`${API_BASE_URL}/api/documents/upload`, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      
      // Start polling for each document
      if (result.documents && result.documents.length > 0) {
        const docIds = result.documents.map(doc => doc.id);
        setProcessingDocs(docIds);
        
        // Notify user that upload is complete and processing started
        if (onShowToast) {
          onShowToast(`${files.length} file(s) uploaded! Processing in the background...`, 'success');
        }
        
        // Start polling for each document
        result.documents.forEach(doc => {
          pollDocumentStatus(doc.id, doc.filename);
        });
      }
      
      if (onUploadSuccess) {
        onUploadSuccess(result);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (onShowToast) onShowToast(`Upload failed: ${error.message}`, 'error');
      
      if (onUploadError) {
        onUploadError(error.message);
      }
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const isEng = mode === 'engineering';
  const uploadTitle = disabled
    ? 'Upload disabled'
    : processingDocs.length > 0
    ? `Processing ${processingDocs.length} document(s)...`
    : isEng
    ? 'Upload engineering document (PDF, DOCX, PNG, JPG) — one at a time'
    : 'Upload documents (PDF, DOCX, TXT, PNG, JPG)';

  return (
    <>
      <button
        className={`upload-button ${isUploading ? 'uploading' : ''}${processingDocs.length > 0 ? ' processing' : ''}`}
        onClick={handleClick}
        disabled={disabled || isUploading}
        title={uploadTitle}
      >
        {isUploading || processingDocs.length > 0 ? <FaSpinner className="spinner" /> : <FaPaperclip />}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple={!isEng}
        accept={isEng ? '.pdf,.docx,.png,.jpg,.jpeg' : '.pdf,.docx,.txt,.png,.jpg,.jpeg'}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default UploadButton;
